import React, { useEffect, useMemo, useState, useRef } from 'react';
import { CalendarIcon, UsersIcon, UserIcon, BriefcaseIcon, TrendingUpIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, ClockIcon, BarChart3Icon, ChevronRightIcon as ArrowRightIcon, ListIcon, UserCheckIcon, FilterIcon, XIcon, CheckIcon } from 'lucide-react';
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { Input } from '../ui/Input';
import { useData } from '../../contexts/DataContext';
import { formatCurrency, formatTime, getToday, getDaysInMonth, getFirstDayOfMonth, getMonthName, getStatusName, getStageName, getStageColor, getDoctorPrefix } from '../../utils/helpers';
import { usePermissions } from '../../hooks/usePermissions';
import { hasPermission } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
interface DashboardProps {
  onNavigate: (module: string) => void;
}
export function Dashboard({
  onNavigate
}: DashboardProps) {
  const {
    patients,
    doctors,
    appointments,
    services,
    opportunities,
    contacts,
    getPatient,
    getDoctor,
    getService,
    getAppointmentsByDate,
    getTodayAppointmentsCount,
    getTotalOpportunitiesAmount
  } = useData();
  const {
    user
  } = useAuth();
  const userRole = user?.role || '';
  const canViewStats = hasPermission(userRole, 'dashboard_stats', 'read');
  const canViewCalendar = hasPermission(userRole, 'dashboard_calendar', 'read');
  const canViewPipeline = hasPermission(userRole, 'dashboard_pipeline', 'read');
  const canViewAppointmentsByUser = hasPermission(userRole, 'dashboard_appointments_by_user', 'read');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(getToday());
  // Appointments by User filter state
  const [aptByUserGroupBy, setAptByUserGroupBy] = useState<'day' | 'month' | 'year'>('month');
  const [aptDateFrom, setAptDateFrom] = useState<string>(() => {
    // Default: 6 months ago
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().split('T')[0];
  });
  const [aptDateTo, setAptDateTo] = useState<string>(getToday());
  const [aptSelectedUsers, setAptSelectedUsers] = useState<string[]>([]);
  const [aptUserDropdownOpen, setAptUserDropdownOpen] = useState(false);
  const aptUserDropdownRef = useRef<HTMLDivElement>(null);
  // Close user dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (aptUserDropdownRef.current && !aptUserDropdownRef.current.contains(e.target as Node)) {
        setAptUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const today = getToday();
  const todayAppointments = getAppointmentsByDate(today);
  const selectedDateAppointments = getAppointmentsByDate(selectedDate);
  // KPIs
  const totalAppointments = appointments.length;
  const todayCount = getTodayAppointmentsCount();
  const activeDoctors = doctors.length;
  const totalServices = services.length;
  const totalContacts = contacts.length;
  const totalOpportunities = opportunities.length;
  // Appointments Statistics
  const appointmentStats = useMemo(() => {
    // Last 7 days trend
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayAppointments = appointments.filter(a => a.date === dateStr);
      last7Days.push({
        date: date.toLocaleDateString('es-ES', {
          weekday: 'short',
          day: 'numeric'
        }),
        total: dayAppointments.length,
        completadas: dayAppointments.filter(a => a.status === 'completada').length,
        programadas: dayAppointments.filter(a => a.status === 'programada').length,
        canceladas: dayAppointments.filter(a => a.status === 'cancelada').length
      });
    }
    // By Doctor
    const byDoctor = appointments.reduce((acc, apt) => {
      const doctor = getDoctor(apt.doctorId);
      const doctorName = doctor?.name || 'Sin asignar';
      acc[doctorName] = (acc[doctorName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const doctorData = Object.entries(byDoctor).map(([name, count]) => ({
      name,
      citas: count
    })).sort((a, b) => b.citas - a.citas).slice(0, 5);
    return {
      last7Days,
      doctorData
    };
  }, [appointments, getDoctor]);
  // Service Detail Statistics (Detalle por Servicio)
  const serviceDetailStats = useMemo(() => {
    const completedAppointments = appointments.filter(a => a.status === 'completada');
    const serviceMap: Record<string, {
      name: string;
      count: number;
      totalRevenue: number;
    }> = {};
    completedAppointments.forEach(apt => {
      const service = getService(apt.serviceId);
      if (service) {
        if (!serviceMap[service.id]) {
          serviceMap[service.id] = {
            name: service.name,
            count: 0,
            totalRevenue: 0
          };
        }
        serviceMap[service.id].count++;
        serviceMap[service.id].totalRevenue += service.price;
      }
    });
    const grandTotalRevenue = Object.values(serviceMap).reduce((sum, s) => sum + s.totalRevenue, 0);
    const grandTotalCount = Object.values(serviceMap).reduce((sum, s) => sum + s.count, 0);
    const details = Object.values(serviceMap).map(s => ({
      ...s,
      avgPerAppointment: s.count > 0 ? s.totalRevenue / s.count : 0,
      percentOfTotal: grandTotalRevenue > 0 ? s.totalRevenue / grandTotalRevenue * 100 : 0
    })).sort((a, b) => b.totalRevenue - a.totalRevenue);
    return {
      details,
      grandTotalRevenue,
      grandTotalCount
    };
  }, [appointments, getService]);
  // Appointments by User Statistics
  const appointmentsByUserStats = useMemo(() => {
    const USER_COLORS = ['#14b8a6', '#8b5cf6', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];
    // Filter appointments by date range
    const filteredApts = appointments.filter(apt => {
      if (aptDateFrom && apt.date < aptDateFrom) return false;
      if (aptDateTo && apt.date > aptDateTo) return false;
      return true;
    });
    // Get all unique users from filtered appointments
    const allUsersSet = new Set<string>();
    filteredApts.forEach(apt => {
      allUsersSet.add(apt.createdByUserName || 'Sin registrar');
    });
    const allAvailableUsers = Array.from(allUsersSet).sort();
    // Apply user filter
    const displayApts = aptSelectedUsers.length > 0 ? filteredApts.filter(apt => {
      const userName = apt.createdByUserName || 'Sin registrar';
      return aptSelectedUsers.includes(userName);
    }) : filteredApts;
    // Group appointments by createdByUserName
    const byUser: Record<string, number> = {};
    displayApts.forEach(apt => {
      const userName = apt.createdByUserName || 'Sin registrar';
      byUser[userName] = (byUser[userName] || 0) + 1;
    });
    const pieData = Object.entries(byUser).map(([name, count], index) => ({
      name,
      value: count,
      color: USER_COLORS[index % USER_COLORS.length]
    })).sort((a, b) => b.value - a.value);
    // Group by time period
    const getKey = (dateStr: string) => {
      const d = new Date(dateStr + 'T12:00:00');
      if (aptByUserGroupBy === 'day') return dateStr;
      if (aptByUserGroupBy === 'year') return String(d.getFullYear());
      // month
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    };
    const getLabel = (key: string) => {
      if (aptByUserGroupBy === 'day') {
        const d = new Date(key + 'T12:00:00');
        return d.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: 'short'
        });
      }
      if (aptByUserGroupBy === 'year') return key;
      // month
      const [y, m] = key.split('-');
      const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      return `${monthNames[parseInt(m) - 1]} ${y}`;
    };
    const timeMap: Record<string, Record<string, number>> = {};
    const displayUsers = new Set<string>();
    displayApts.forEach(apt => {
      const userName = apt.createdByUserName || 'Sin registrar';
      displayUsers.add(userName);
      const key = getKey(apt.date);
      if (!timeMap[key]) timeMap[key] = {};
      timeMap[key][userName] = (timeMap[key][userName] || 0) + 1;
    });
    const timeData = Object.entries(timeMap).sort(([a], [b]) => a.localeCompare(b)).map(([period, users]) => {
      const entry: Record<string, any> = {
        period: getLabel(period)
      };
      displayUsers.forEach(u => {
        entry[u] = users[u] || 0;
      });
      return entry;
    });
    // Limit to last 15 entries for readability
    const limitedTimeData = timeData.slice(-15);
    const userList = Array.from(displayUsers);
    // Total count for filtered results
    const totalFiltered = displayApts.length;
    return {
      pieData,
      timeData: limitedTimeData,
      userList,
      USER_COLORS,
      allAvailableUsers,
      totalFiltered
    };
  }, [appointments, aptByUserGroupBy, aptDateFrom, aptDateTo, aptSelectedUsers]);
  // Pipeline stages
  const pipelineStages = [{
    id: 'nuevo_lead',
    name: 'Nuevo Lead',
    color: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50 dark:bg-blue-900/20',
    textColor: 'text-blue-600 dark:text-blue-400'
  }, {
    id: 'contactado',
    name: 'Contactado',
    color: 'from-cyan-500 to-cyan-600',
    bgLight: 'bg-cyan-50 dark:bg-cyan-900/20',
    textColor: 'text-cyan-600 dark:text-cyan-400'
  }, {
    id: 'evaluacion_consulta',
    name: 'Evaluación / Consulta',
    color: 'from-purple-500 to-purple-600',
    bgLight: 'bg-purple-50 dark:bg-purple-900/20',
    textColor: 'text-purple-600 dark:text-purple-400'
  }, {
    id: 'cotizacion_enviada',
    name: 'Cotización Enviada',
    color: 'from-amber-500 to-amber-600',
    bgLight: 'bg-amber-50 dark:bg-amber-900/20',
    textColor: 'text-amber-600 dark:text-amber-400'
  }, {
    id: 'confirmado',
    name: 'Confirmado',
    color: 'from-emerald-500 to-emerald-600',
    bgLight: 'bg-emerald-50 dark:bg-emerald-900/20',
    textColor: 'text-emerald-600 dark:text-emerald-400'
  }, {
    id: 'cita_agendada',
    name: 'Cita Agendada',
    color: 'from-teal-500 to-teal-600',
    bgLight: 'bg-teal-50 dark:bg-teal-900/20',
    textColor: 'text-teal-600 dark:text-teal-400'
  }, {
    id: 'servicio_realizado',
    name: 'Servicio Realizado',
    color: 'from-green-500 to-green-600',
    bgLight: 'bg-green-50 dark:bg-green-900/20',
    textColor: 'text-green-600 dark:text-green-400'
  }];
  const getStageCount = (stage: string) => {
    return opportunities.filter(o => o.stage === stage).length;
  };
  const getStageAmount = (stage: string) => {
    return opportunities.filter(o => o.stage === stage).reduce((sum, o) => sum + o.amount, 0);
  };
  // Calendar
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const calendarDays: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push(i);
  }
  const getDateString = (day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };
  const getAppointmentCountForDay = (day: number) => {
    const dateStr = getDateString(day);
    return appointments.filter(a => a.date === dateStr && a.status !== 'cancelada').length;
  };
  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };
  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };
  // Recent appointments
  const recentAppointments = [...appointments].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5);
  // Format today's date nicely in Spanish
  const formattedDate = new Date().toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  // Status color mapping for appointment cards
  const getStatusAccentColor = (status: string) => {
    switch (status) {
      case 'programada':
        return 'bg-teal-500';
      case 'completada':
        return 'bg-emerald-500';
      case 'cancelada':
        return 'bg-rose-500';
      case 'reprogramada':
        return 'bg-amber-500';
      default:
        return 'bg-gray-400';
    }
  };
  return <div className="space-y-6" data-id="element-932">
      {/* Welcome Hero Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 p-6 md:p-8 text-white shadow-xl" data-id="element-933">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-934" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-400/20 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4" data-id="element-935" />
        <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-teal-300/10 rounded-full blur-xl" data-id="element-936" />

        <div className="relative z-10" data-id="element-937">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6" data-id="element-938">
            <div data-id="element-939">
              <div className="flex items-center gap-2 mb-2" data-id="element-940">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-teal-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-941">
                  <path d="M12 21c-4-4-8-6.5-8-10.5a4.5 4.5 0 0 1 9 0 4.5 4.5 0 0 1 9 0c0 4-4 6.5-8 10.5" data-id="element-942" />
                  <path d="M12 6c0-2.5 2-4 4-4" data-id="element-943" />
                  <path d="M12 6c0-2.5-2-4-4-4" data-id="element-944" />
                </svg>
                <span className="text-teal-100 text-sm font-medium capitalize" data-id="element-945">
                  {formattedDate}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-946">
                {user?.gender === 'femenino' ? 'Bienvenida' : 'Bienvenido'},{' '}
                {user?.name?.split(' ')[0] || 'Usuario'}
              </h1>
              <p className="text-teal-100 text-lg" data-id="element-947">
                Panel de control de Savidna
              </p>

              {/* Quick stats pills */}
              <div className="flex flex-wrap gap-3 mt-4" data-id="element-948">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full border border-white/20" data-id="element-949">
                  <ClockIcon className="w-4 h-4" data-id="element-950" />
                  <span className="font-semibold" data-id="element-951">{todayCount}</span>
                  <span className="text-teal-100 text-sm" data-id="element-952">citas hoy</span>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full border border-white/20" data-id="element-953">
                  <UsersIcon className="w-4 h-4" data-id="element-954" />
                  <span className="font-semibold" data-id="element-955">{patients.length}</span>
                  <span className="text-teal-100 text-sm" data-id="element-956">pacientes</span>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3" data-id="element-957">
              {hasPermission(userRole, 'patients', 'full') && <Button variant="outline" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-959" />} onClick={() => onNavigate('patients')} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-958">
                  Nuevo Paciente
                </Button>}
              {hasPermission(userRole, 'appointments', 'full') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-961" />} onClick={() => onNavigate('appointments')} className="bg-white text-teal-700 hover:bg-teal-50 shadow-lg shadow-teal-900/20" data-id="element-960">
                  Nueva Cita
                </Button>}
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards - Only if stats permission */}
      {canViewStats && <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-962">
          {[{
        icon: CalendarIcon,
        value: totalAppointments,
        label: 'Citas Totales',
        color: 'teal',
        gradient: 'from-teal-500 to-cyan-500',
        module: 'appointments'
      }, {
        icon: ClockIcon,
        value: todayCount,
        label: 'Citas Hoy',
        color: 'emerald',
        gradient: 'from-emerald-500 to-green-500',
        module: 'appointments'
      }, {
        icon: UserIcon,
        value: activeDoctors,
        label: 'Doctores',
        color: 'purple',
        gradient: 'from-purple-500 to-indigo-500',
        module: 'doctors'
      }, {
        icon: BriefcaseIcon,
        value: totalServices,
        label: 'Servicios',
        color: 'orange',
        gradient: 'from-orange-500 to-amber-500',
        module: 'services'
      }, {
        icon: UsersIcon,
        value: totalContacts,
        label: 'Contactos',
        color: 'yellow',
        gradient: 'from-yellow-500 to-amber-500',
        module: 'contacts'
      }, {
        icon: TrendingUpIcon,
        value: totalOpportunities,
        label: 'Oportunidades',
        color: 'rose',
        gradient: 'from-rose-500 to-pink-500',
        module: 'opportunities'
      }].map((kpi, index) => {
        const Icon = kpi.icon;
        return <Card key={index} className="cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden group" onClick={() => onNavigate(kpi.module)} data-id="element-963">
                <div className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${kpi.gradient}`} data-id="element-964" />
                <div className="flex items-center gap-3 pl-3" data-id="element-965">
                  <div className={`p-2.5 rounded-xl bg-gradient-to-br ${kpi.gradient} shadow-lg`} data-id="element-966">
                    <Icon className="w-5 h-5 text-white" data-id="element-967" />
                  </div>
                  <div data-id="element-968">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-969">
                      {kpi.value}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium" data-id="element-970">
                      {kpi.label}
                    </p>
                  </div>
                </div>
              </Card>;
      })}
        </div>}

      {/* Appointments Statistics Charts - Only if stats permission */}
      {canViewStats && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-971">
          {/* 7 Days Trend - Area Chart */}
          <Card padding="none" className="overflow-hidden" data-id="element-972">
            <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-973" />
            <div className="p-4 md:p-6" data-id="element-974">
              <CardHeader className="px-0 pt-0" data-id="element-975">
                <CardTitle className="flex items-center gap-2" data-id="element-976">
                  <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30" data-id="element-977">
                    <TrendingUpIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" data-id="element-978" />
                  </div>
                  Tendencia Últimos 7 Días
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0" data-id="element-979">
                <ResponsiveContainer width="100%" height={280} data-id="element-980">
                  <AreaChart data={appointmentStats.last7Days} data-id="element-981">
                    <defs data-id="element-982">
                      <linearGradient id="colorCompletadas" x1="0" y1="0" x2="0" y2="1" data-id="element-983">
                        <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3} data-id="element-984" />
                        <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} data-id="element-985" />
                      </linearGradient>
                      <linearGradient id="colorProgramadas" x1="0" y1="0" x2="0" y2="1" data-id="element-986">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} data-id="element-987" />
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} data-id="element-988" />
                      </linearGradient>
                      <linearGradient id="colorCanceladas" x1="0" y1="0" x2="0" y2="1" data-id="element-989">
                        <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} data-id="element-990" />
                        <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} data-id="element-991" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-992" />
                    <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} data-id="element-993" />
                    <YAxis stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} data-id="element-994" />
                    <Tooltip contentStyle={{
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                  padding: '12px 16px'
                }} data-id="element-995" />
                    <Legend wrapperStyle={{
                  paddingTop: '20px'
                }} data-id="element-996" />
                    <Area type="monotone" dataKey="completadas" stroke="#14b8a6" strokeWidth={2} fill="url(#colorCompletadas)" name="Completadas" data-id="element-997" />
                    <Area type="monotone" dataKey="programadas" stroke="#0ea5e9" strokeWidth={2} fill="url(#colorProgramadas)" name="Programadas" data-id="element-998" />
                    <Area type="monotone" dataKey="canceladas" stroke="#f43f5e" strokeWidth={2} fill="url(#colorCanceladas)" name="Canceladas" data-id="element-999" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </div>
          </Card>

          {/* Top Doctors by Appointments */}
          <Card padding="none" className="overflow-hidden" data-id="element-1000">
            <div className="h-1 bg-gradient-to-r from-purple-500 to-indigo-500" data-id="element-1001" />
            <div className="p-4 md:p-6" data-id="element-1002">
              <CardHeader className="px-0 pt-0" data-id="element-1003">
                <CardTitle className="flex items-center gap-2" data-id="element-1004">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30" data-id="element-1005">
                    <UserIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-1006" />
                  </div>
                  Top Doctores por Citas
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0" data-id="element-1007">
                <ResponsiveContainer width="100%" height={280} data-id="element-1008">
                  <BarChart data={appointmentStats.doctorData} layout="vertical" data-id="element-1009">
                    <defs data-id="element-1010">
                      <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0" data-id="element-1011">
                        <stop offset="0%" stopColor="#8b5cf6" data-id="element-1012" />
                        <stop offset="100%" stopColor="#6366f1" data-id="element-1013" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={true} vertical={false} data-id="element-1014" />
                    <XAxis type="number" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} data-id="element-1015" />
                    <YAxis dataKey="name" type="category" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} width={100} data-id="element-1016" />
                    <Tooltip contentStyle={{
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                  padding: '12px 16px'
                }} data-id="element-1017" />
                    <Bar dataKey="citas" fill="url(#barGradient)" radius={[0, 8, 8, 0]} name="Citas" data-id="element-1018" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </div>
          </Card>
        </div>}

      {/* Pipeline - Only if pipeline permission */}
      {canViewPipeline && <Card padding="none" className="overflow-hidden" data-id="element-1019">
          <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500" data-id="element-1020" />
          <div className="p-4 md:p-6" data-id="element-1021">
            <CardHeader className="px-0 pt-0" data-id="element-1022">
              <CardTitle className="flex items-center gap-2" data-id="element-1023">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30" data-id="element-1024">
                  <TrendingUpIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1025" />
                </div>
                Pipeline de Ingresos
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-1026">
              <div className="flex gap-2 overflow-x-auto pb-2" data-id="element-1027">
                {pipelineStages.map((stage, index) => {
              const count = getStageCount(stage.id);
              const amount = getStageAmount(stage.id);
              return <div key={stage.id} className="flex items-center" data-id="element-1028">
                      <div className={`flex-1 min-w-[140px] p-4 ${stage.bgLight} rounded-xl border border-gray-100 dark:border-gray-700/50 relative overflow-hidden`} data-id="element-1029">
                        <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${stage.color}`} data-id="element-1030" />
                        <div className="flex items-center gap-2 mb-2" data-id="element-1031">
                          <div className={`w-2.5 h-2.5 rounded-full bg-gradient-to-r ${stage.color}`} data-id="element-1032" />
                          <span className={`text-sm font-semibold ${stage.textColor}`} data-id="element-1033">
                            {stage.name}
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1034">
                          {count}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1035">
                          {formatCurrency(amount)}
                        </p>
                      </div>
                      {index < pipelineStages.length - 1 && <ArrowRightIcon className="w-5 h-5 text-gray-300 dark:text-gray-600 mx-1 flex-shrink-0" data-id="element-1036" />}
                    </div>;
            })}
              </div>
            </CardContent>
          </div>
        </Card>}

      {/* Appointments by User - Only if permission */}
      {canViewAppointmentsByUser && <div className="space-y-6" data-id="element-1037">
          {/* Filters Card */}
         <Card padding="none" className="overflow-visible relative z-30" data-id="element-1038">
            <div className="h-1 bg-gradient-to-r from-indigo-500 to-violet-500" data-id="element-1039" />
            <div className="p-4 md:p-6" data-id="element-1040">
              <div className="flex items-center gap-2 mb-4" data-id="element-1041">
                <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30" data-id="element-1042">
                  <UserCheckIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" data-id="element-1043" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-1044">
                  Citas Creadas por Usuario
                </h3>
                <span className="ml-auto text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1 rounded-full" data-id="element-1045">
                  {appointmentsByUserStats.totalFiltered} citas
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end" data-id="element-1046">
                {/* Date Range */}
                <div className="md:col-span-5" data-id="element-1047">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1048">
                    Rango de Fecha
                  </label>
                  <div className="flex items-center gap-2" data-id="element-1049">
                    <input type="date" value={aptDateFrom} onChange={e => setAptDateFrom(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors" data-id="element-1050" />
                    <span className="text-gray-400 font-medium" data-id="element-1051">–</span>
                    <input type="date" value={aptDateTo} onChange={e => setAptDateTo(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors" data-id="element-1052" />
                  </div>
                </div>

                {/* Group By */}
                <div className="md:col-span-3" data-id="element-1053">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1054">
                    Agrupar Por
                  </label>
                  <Select options={[{
                value: 'day',
                label: 'Día'
              }, {
                value: 'month',
                label: 'Mes'
              }, {
                value: 'year',
                label: 'Año'
              }]} value={aptByUserGroupBy} onChange={v => setAptByUserGroupBy(v as 'day' | 'month' | 'year')} clearable={false} searchable={false} data-id="element-1055" />
                </div>

                {/* User Multi-Select */}
                <div className="md:col-span-4" ref={aptUserDropdownRef} data-id="element-1056">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1057">
                    Usuarios
                  </label>
                  <div className="relative" data-id="element-1058">
                    <div onClick={() => setAptUserDropdownOpen(!aptUserDropdownOpen)} className={`
                        w-full px-3 py-2 rounded-lg border cursor-pointer
                        bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                        transition-colors duration-150 flex items-center justify-between min-h-[42px]
                        ${aptUserDropdownOpen ? 'border-indigo-500 ring-2 ring-indigo-500' : 'border-gray-300 dark:border-gray-600'}
                      `} data-id="element-1059">
                      <div className="flex-1 flex flex-wrap gap-1 min-w-0" data-id="element-1060">
                        {aptSelectedUsers.length === 0 ? <span className="text-gray-400 dark:text-gray-500 text-sm" data-id="element-1061">
                            Todos los usuarios
                          </span> : aptSelectedUsers.map(u => <span key={u} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-medium max-w-[120px]" data-id="element-1062">
                              <span className="truncate" data-id="element-1063">{u}</span>
                              <button onClick={e => {
                        e.stopPropagation();
                        setAptSelectedUsers(prev => prev.filter(x => x !== u));
                      }} className="hover:text-indigo-900 dark:hover:text-indigo-100 flex-shrink-0" type="button" data-id="element-1064">
                                <XIcon className="w-3 h-3" data-id="element-1065" />
                              </button>
                            </span>)}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2" data-id="element-1066">
                        {aptSelectedUsers.length > 0 && <button onClick={e => {
                      e.stopPropagation();
                      setAptSelectedUsers([]);
                    }} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" type="button" data-id="element-1067">
                            <XIcon className="w-4 h-4 text-gray-400" data-id="element-1068" />
                          </button>}
                        <ChevronRightIcon className={`w-4 h-4 text-gray-400 transition-transform ${aptUserDropdownOpen ? 'rotate-90' : ''}`} data-id="element-1069" />
                      </div>
                    </div>

                    {aptUserDropdownOpen && <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-id="element-1070">
                        {appointmentsByUserStats.allAvailableUsers.length === 0 ? <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 text-center" data-id="element-1071">
                            No hay usuarios en este rango
                          </div> : <>
                            {/* Select All / Deselect All */}
                            <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex gap-2" data-id="element-1072">
                              <button onClick={() => setAptSelectedUsers([...appointmentsByUserStats.allAvailableUsers])} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-medium" type="button" data-id="element-1073">
                                Seleccionar todos
                              </button>
                              <span className="text-gray-300 dark:text-gray-600" data-id="element-1074">
                                |
                              </span>
                              <button onClick={() => setAptSelectedUsers([])} className="text-xs text-gray-500 dark:text-gray-400 hover:underline font-medium" type="button" data-id="element-1075">
                                Limpiar
                              </button>
                            </div>
                            {appointmentsByUserStats.allAvailableUsers.map(userName => {
                      const isSelected = aptSelectedUsers.includes(userName);
                      return <div key={userName} onClick={() => {
                        setAptSelectedUsers(prev => isSelected ? prev.filter(x => x !== userName) : [...prev, userName]);
                      }} className={`
                                    px-3 py-2.5 cursor-pointer flex items-center gap-3
                                    hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors
                                    ${isSelected ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''}
                                  `} data-id="element-1076">
                                    <div className={`
                                    w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors
                                    ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 dark:border-gray-600'}
                                  `} data-id="element-1077">
                                      {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-1078" />}
                                    </div>
                                    <div className="flex items-center gap-2 min-w-0" data-id="element-1079">
                                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0" data-id="element-1080">
                                        {userName.charAt(0).toUpperCase()}
                                      </div>
                                      <span className="text-sm text-gray-900 dark:text-gray-100 truncate" data-id="element-1081">
                                        {userName}
                                      </span>
                                    </div>
                                  </div>;
                    })}
                          </>}
                      </div>}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1082">
            {/* Bar Chart - Appointments by User over time */}
            <Card padding="none" className="overflow-hidden lg:col-span-2" data-id="element-1083">
              <div className="h-1 bg-gradient-to-r from-indigo-500 to-violet-500" data-id="element-1084" />
              <div className="p-4 md:p-6" data-id="element-1085">
                <CardHeader className="px-0 pt-0" data-id="element-1086">
                  <CardTitle className="flex items-center gap-2 text-base" data-id="element-1087">
                    <BarChart3Icon className="w-5 h-5 text-indigo-500" data-id="element-1088" />
                    Citas por Período
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-0 pb-0" data-id="element-1089">
                  {appointmentsByUserStats.timeData.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700" data-id="element-1090">
                      <UserCheckIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-1091" />
                      <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-1092">
                        No hay datos en este rango
                      </p>
                      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-1093">
                        Ajusta las fechas o los usuarios seleccionados
                      </p>
                    </div> : <ResponsiveContainer width="100%" height={320} data-id="element-1094">
                      <BarChart data={appointmentsByUserStats.timeData} data-id="element-1095">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-1096" />
                        <XAxis dataKey="period" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} angle={aptByUserGroupBy === 'day' ? -45 : 0} textAnchor={aptByUserGroupBy === 'day' ? 'end' : 'middle'} height={aptByUserGroupBy === 'day' ? 60 : 30} data-id="element-1097" />
                        <YAxis stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} data-id="element-1098" />
                        <Tooltip contentStyle={{
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                    padding: '12px 16px'
                  }} data-id="element-1099" />
                        <Legend wrapperStyle={{
                    paddingTop: '16px'
                  }} data-id="element-1100" />
                        {appointmentsByUserStats.userList.map((userName, index) => <Bar key={userName} dataKey={userName} name={userName} fill={appointmentsByUserStats.USER_COLORS[index % appointmentsByUserStats.USER_COLORS.length]} radius={[4, 4, 0, 0]} stackId="users" data-id="element-1101" />)}
                      </BarChart>
                    </ResponsiveContainer>}
                </CardContent>
              </div>
            </Card>

            {/* Pie Chart - Total by User */}
            <Card padding="none" className="overflow-hidden" data-id="element-1102">
              <div className="h-1 bg-gradient-to-r from-violet-500 to-purple-500" data-id="element-1103" />
              <div className="p-4 md:p-6" data-id="element-1104">
                <CardHeader className="px-0 pt-0" data-id="element-1105">
                  <CardTitle className="flex items-center gap-2 text-base" data-id="element-1106">
                    <UsersIcon className="w-5 h-5 text-violet-500" data-id="element-1107" />
                    Total por Usuario
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-0 pb-0" data-id="element-1108">
                  {appointmentsByUserStats.pieData.length === 0 ? <div className="text-center py-8 text-gray-400" data-id="element-1109">
                      Sin datos
                    </div> : <>
                      <ResponsiveContainer width="100%" height={200} data-id="element-1110">
                        <PieChart data-id="element-1111">
                          <Pie data={appointmentsByUserStats.pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" data-id="element-1112">
                            {appointmentsByUserStats.pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1113" />)}
                          </Pie>
                          <Tooltip contentStyle={{
                      backgroundColor: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                      padding: '8px 12px'
                    }} formatter={(value: number, name: string) => [`${value} citas`, name]} data-id="element-1114" />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-2 mt-4" data-id="element-1115">
                        {appointmentsByUserStats.pieData.map((item, index) => <div key={index} className="flex items-center justify-between text-sm" data-id="element-1116">
                            <div className="flex items-center gap-2" data-id="element-1117">
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{
                        backgroundColor: item.color
                      }} data-id="element-1118" />
                              <span className="text-gray-700 dark:text-gray-300 truncate max-w-[140px]" data-id="element-1119">
                                {item.name}
                              </span>
                            </div>
                            <span className="font-semibold text-gray-900 dark:text-white" data-id="element-1120">
                              {item.value}
                            </span>
                          </div>)}
                      </div>
                    </>}
                </CardContent>
              </div>
            </Card>
          </div>
        </div>}

      {/* Calendar and Appointments - Only if calendar permission */}
      {canViewCalendar && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1121">
          {/* Mini Calendar */}
          <Card padding="none" className="overflow-hidden" data-id="element-1122">
            <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-1123" />
            <div className="p-4 md:p-6" data-id="element-1124">
              <CardHeader className="px-0 pt-0" data-id="element-1125">
                <div className="flex items-center justify-between" data-id="element-1126">
                  <CardTitle className="flex items-center gap-2" data-id="element-1127">
                    <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30" data-id="element-1128">
                      <CalendarIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" data-id="element-1129" />
                    </div>
                    Calendario
                  </CardTitle>
                  <div className="flex items-center gap-1" data-id="element-1130">
                    <button onClick={prevMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-1131">
                      <ChevronLeftIcon className="w-5 h-5 text-gray-500" data-id="element-1132" />
                    </button>
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 min-w-[130px] text-center" data-id="element-1133">
                      {getMonthName(month)} {year}
                    </span>
                    <button onClick={nextMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-1134">
                      <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-1135" />
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-0" data-id="element-1136">
                <div className="grid grid-cols-7 gap-1 mb-3" data-id="element-1137">
                  {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => <div key={day} className="text-center text-xs font-semibold text-gray-400 dark:text-gray-500 py-2" data-id="element-1138">
                        {day}
                      </div>)}
                </div>
                <div className="grid grid-cols-7 gap-1" data-id="element-1139">
                  {calendarDays.map((day, index) => {
                if (day === null) {
                  return <div key={`empty-${index}`} className="aspect-square" data-id="element-1140" />;
                }
                const dateStr = getDateString(day);
                const appointmentCount = getAppointmentCountForDay(day);
                const isToday = dateStr === today;
                const isSelected = dateStr === selectedDate;
                return <button key={day} onClick={() => setSelectedDate(dateStr)} className={`
                          aspect-square rounded-xl text-sm font-medium relative
                          flex flex-col items-center justify-center gap-0.5
                          transition-all duration-200
                          ${isToday ? 'bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30' : ''}
                          ${isSelected && !isToday ? 'ring-2 ring-teal-500 ring-offset-2 dark:ring-offset-gray-800 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300' : ''}
                          ${!isToday && !isSelected ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : ''}
                        `} data-id="element-1141">
                        <span data-id="element-1142">{day}</span>
                        {appointmentCount > 0 && <div className={`w-1.5 h-1.5 rounded-full ${isToday ? 'bg-white' : 'bg-teal-500'}`} data-id="element-1143" />}
                      </button>;
              })}
                </div>
              </CardContent>
            </div>
          </Card>

          {/* Today's Appointments */}
          <Card padding="none" className="overflow-hidden" data-id="element-1144">
            <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500" data-id="element-1145" />
            <div className="p-4 md:p-6" data-id="element-1146">
              <CardHeader className="px-0 pt-0" data-id="element-1147">
                <CardTitle className="flex items-center gap-2" data-id="element-1148">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30" data-id="element-1149">
                    <ClockIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" data-id="element-1150" />
                  </div>
                  Citas del {selectedDate === today ? 'Hoy' : selectedDate}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0" data-id="element-1151">
                {selectedDateAppointments.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700" data-id="element-1152">
                    <CalendarIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-1153" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-1154">
                      No hay citas programadas
                    </p>
                  </div> : <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1" data-id="element-1155">
                    {selectedDateAppointments.map(apt => {
                const patient = getPatient(apt.patientId);
                const doctor = getDoctor(apt.doctorId);
                const service = getService(apt.serviceId);
                return <div key={apt.id} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50 relative overflow-hidden" data-id="element-1156">
                          <div className={`absolute left-0 top-0 bottom-0 w-1 ${getStatusAccentColor(apt.status)}`} data-id="element-1157" />
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-teal-500/20 flex-shrink-0" data-id="element-1158">
                            {patient?.name?.charAt(0).toUpperCase() || 'P'}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1159">
                            <div className="flex items-center gap-2 mb-1" data-id="element-1160">
                              <span className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-1161">
                                {formatTime(apt.time)}
                              </span>
                              <span className="text-sm text-gray-400" data-id="element-1162">•</span>
                              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1163">
                                {apt.duration} min
                              </span>
                            </div>
                            <p className="font-semibold text-gray-900 dark:text-white truncate" data-id="element-1164">
                              {patient?.name || 'Paciente'}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate" data-id="element-1165">
                              {doctor ? `${getDoctorPrefix(doctor.gender)} ${doctor.name}` : ''}{' '}
                              • {service?.name}
                            </p>
                          </div>
                          <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-1166">
                            {getStatusName(apt.status)}
                          </Badge>
                        </div>;
              })}
                  </div>}
              </CardContent>
            </div>
          </Card>
        </div>}

      {/* Recent Appointments - Only if stats permission */}
      {canViewStats && <Card padding="none" className="overflow-hidden" data-id="element-1167">
          <div className="h-1 bg-gradient-to-r from-gray-400 to-gray-500" data-id="element-1168" />
          <div className="p-4 md:p-6" data-id="element-1169">
            <CardHeader className="px-0 pt-0" data-id="element-1170">
              <div className="flex items-center justify-between" data-id="element-1171">
                <CardTitle className="flex items-center gap-2" data-id="element-1172">
                  <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700" data-id="element-1173">
                    <CalendarIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" data-id="element-1174" />
                  </div>
                  Citas Recientes
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => onNavigate('appointments')} className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300" data-id="element-1175">
                  Ver todas
                  <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-1176" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-1177">
              <div className="overflow-x-auto" data-id="element-1178">
                <table className="w-full" data-id="element-1179">
                  <thead data-id="element-1180">
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-1181">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1182">
                        Paciente
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1183">
                        Doctor
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1184">
                        Servicio
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1185">
                        Fecha
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1186">
                        Hora
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1187">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-1188">
                    {recentAppointments.map(apt => {
                  const patient = getPatient(apt.patientId);
                  const doctor = getDoctor(apt.doctorId);
                  const service = getService(apt.serviceId);
                  return <tr key={apt.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-1189">
                          <td className="py-3 px-4" data-id="element-1190">
                            <div className="flex items-center gap-3" data-id="element-1191">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white text-sm font-semibold" data-id="element-1192">
                                {patient?.name?.charAt(0).toUpperCase() || 'P'}
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-1193">
                                {patient?.name || '-'}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-1194">
                            Dr.{' '}
                            {doctor ? `${getDoctorPrefix(doctor.gender)} ${doctor.name}` : '-'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-1195">
                            {service?.name || '-'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-1196">
                            {apt.date}
                          </td>
                          <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-1197">
                            {formatTime(apt.time)}
                          </td>
                          <td className="py-3 px-4" data-id="element-1198">
                            <div className="flex items-center gap-2" data-id="element-1199">
                              <div className={`w-2 h-2 rounded-full ${getStatusAccentColor(apt.status)}`} data-id="element-1200" />
                              <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1201">
                                {getStatusName(apt.status)}
                              </span>
                            </div>
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </div>
        </Card>}

      {/* Detalle por Servicio - Only if stats permission */}
      {canViewStats && <Card padding="none" className="overflow-hidden" data-id="element-1202">
          <div className="h-1 bg-gradient-to-r from-orange-500 to-amber-500" data-id="element-1203" />
          <div className="p-4 md:p-6" data-id="element-1204">
            <CardHeader className="px-0 pt-0" data-id="element-1205">
              <div className="flex items-center justify-between" data-id="element-1206">
                <CardTitle className="flex items-center gap-2" data-id="element-1207">
                  <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30" data-id="element-1208">
                    <ListIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" data-id="element-1209" />
                  </div>
                  Detalle por Servicio
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => onNavigate('services')} className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300" data-id="element-1210">
                  Ver servicios
                  <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-1211" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-1212">
              {serviceDetailStats.details.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700" data-id="element-1213">
                  <BriefcaseIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-1214" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-1215">
                    No hay citas completadas aún
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-1216">
                    Los datos aparecerán cuando se completen citas
                  </p>
                </div> : <div className="overflow-x-auto" data-id="element-1217">
                  <table className="w-full" data-id="element-1218">
                    <thead data-id="element-1219">
                      <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-1220">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1221">
                          Servicio
                        </th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1222">
                          Cantidad
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1223">
                          Ingresos Totales
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1224">
                          Promedio / Cita
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1225">
                          % del Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-1226">
                      {serviceDetailStats.details.map((service, index) => <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-1227">
                          <td className="py-3 px-4" data-id="element-1228">
                            <div className="flex items-center gap-3" data-id="element-1229">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-sm font-semibold" data-id="element-1230">
                                {service.name.charAt(0)}
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-1231">
                                {service.name}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center" data-id="element-1232">
                            <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 text-sm font-semibold" data-id="element-1233">
                              {service.count}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right text-sm font-semibold text-gray-900 dark:text-gray-100" data-id="element-1234">
                            {formatCurrency(service.totalRevenue)}
                          </td>
                          <td className="py-3 px-4 text-right text-sm text-gray-600 dark:text-gray-400" data-id="element-1235">
                            {formatCurrency(service.avgPerAppointment)}
                          </td>
                          <td className="py-3 px-4 text-right" data-id="element-1236">
                            <div className="flex items-center justify-end gap-2" data-id="element-1237">
                              <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-1238">
                                <div className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full transition-all duration-500" style={{
                          width: `${Math.min(service.percentOfTotal, 100)}%`
                        }} data-id="element-1239" />
                              </div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[3rem] text-right" data-id="element-1240">
                                {service.percentOfTotal.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                    {/* Totals Row */}
                    <tfoot data-id="element-1241">
                      <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-800/80" data-id="element-1242">
                        <td className="py-3 px-4 text-sm font-bold text-gray-900 dark:text-white" data-id="element-1243">
                          TOTAL
                        </td>
                        <td className="py-3 px-4 text-center text-sm font-bold text-gray-900 dark:text-white" data-id="element-1244">
                          {serviceDetailStats.grandTotalCount}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-900 dark:text-white" data-id="element-1245">
                          {formatCurrency(serviceDetailStats.grandTotalRevenue)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-600 dark:text-gray-400" data-id="element-1246">
                          {serviceDetailStats.grandTotalCount > 0 ? formatCurrency(serviceDetailStats.grandTotalRevenue / serviceDetailStats.grandTotalCount) : formatCurrency(0)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-900 dark:text-white" data-id="element-1247">
                          100%
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>}
            </CardContent>
          </div>
        </Card>}

      {/* Show message if no sections are visible */}
      {!canViewStats && !canViewCalendar && !canViewPipeline && !canViewAppointmentsByUser && <Card className="overflow-hidden" data-id="element-1248">
            <div className="py-16 text-center" data-id="element-1249">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-100 dark:from-teal-900/30 dark:to-cyan-900/30 flex items-center justify-center" data-id="element-1250">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-teal-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-1251">
                  <path d="M12 21c-4-4-8-6.5-8-10.5a4.5 4.5 0 0 1 9 0 4.5 4.5 0 0 1 9 0c0 4-4 6.5-8 10.5" data-id="element-1252" />
                  <path d="M12 6c0-2.5 2-4 4-4" data-id="element-1253" />
                  <path d="M12 6c0-2.5-2-4-4-4" data-id="element-1254" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-1255">
                {user?.gender === 'femenino' ? 'Bienvenida' : 'Bienvenido'} al
                Panel de Control
              </h2>
              <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto" data-id="element-1256">
                No tienes acceso a las secciones avanzadas del panel. Contacta
                al administrador para solicitar permisos adicionales.
              </p>
            </div>
          </Card>}
    </div>;
}